import { VCheckboxBtn, VInput } from 'vuetify/components'

// VInput is the internal wrapper every Vuetify form control (VTextField,
// VTextarea, VSelect, VCheckbox, VRadio, VSwitch, ...) renders around itself
// to compute isDirty/isActive (which drives floating labels, prefix/suffix
// opacity, and validation state). None of those parent components disable
// COMPONENT_V_MODEL on VInput itself, so Vue's legacy v-model compat rewrite
// silently strips the modelValue it's passed internally, leaving isDirty
// permanently false. This must run before any Compat component renders, so
// it lives here since every input-family compat file imports this module.
VInput.compatConfig = {
  ...(VInput.compatConfig || {}),
  COMPONENT_V_MODEL: false,
  INSTANCE_ATTRS_CLASS_STYLE: false
}

// Same bug, different internal component: VCheckbox.js doesn't render
// VSelectionControl directly (unlike VRadio/VSwitch) - it goes through an
// extra VCheckboxBtn wrapper (VCheckbox.js -> VCheckboxBtn.js ->
// VSelectionControl), and VSelectionControlCompat.js only ever patched
// VSelectionControl/VSelectionControlGroup/VCheckbox themselves, never this
// middle layer. The legacy v-model rewrite strips VCheckboxBtn's internal
// modelValue, so clicking a checkbox visually toggles the native input but
// never reaches back up through VCheckboxBtn to VCheckbox's own model - a
// v-model'd <v-checkbox> silently never updates its bound value.
VCheckboxBtn.compatConfig = {
  ...(VCheckboxBtn.compatConfig || {}),
  COMPONENT_V_MODEL: false,
  INSTANCE_ATTRS_CLASS_STYLE: false
}

// NOTE: Vuetify's mdi iconset (iconsets/mdi.js) renders any icon set via an
// `icon` PROP (as opposed to typed as <v-icon>mdi-x</v-icon> slot content -
// radio/checkbox glyphs, select dropdown arrows, clear buttons, pagination
// arrows, etc.) through an anonymous inline function component with no
// declared props schema, which explicitly overwrites its class to the bare
// glyph class. Because that wrapper has no props schema, Vue has no
// attrs/props split to fall class through at that call, so the 'v-icon' base
// class from the outer VIcon call is lost before it ever reaches a component
// we can patch via compatConfig - there's no exported binding for that inline
// function to target. This is restored visually via CSS instead, see
// `_vuetify-legacy.scss` (search "i.mdi:not(.v-icon)").

function hasBooleanishAttr(value) {
  return value === '' || value === true || value === 'true'
}

function resolveVariant(attrs = {}) {
  if (attrs.variant != null) return attrs.variant
  if (
    hasBooleanishAttr(attrs.solo) ||
    hasBooleanishAttr(attrs['solo-inverted'])
  ) {
    return 'solo'
  }
  if (hasBooleanishAttr(attrs.outlined)) return 'outlined'
  if (hasBooleanishAttr(attrs.filled)) return 'filled'
  if (hasBooleanishAttr(attrs.plain)) return 'plain'
  if (hasBooleanishAttr(attrs.underlined)) return 'underlined'
  if (hasBooleanishAttr(attrs.flat)) return 'plain'
  return 'underlined'
}

function resolveDensity(attrs = {}) {
  if (attrs.density != null) return attrs.density
  if (hasBooleanishAttr(attrs.dense)) return 'compact'
  return undefined
}

function resolveBgColor(attrs = {}) {
  return attrs.bgColor ?? attrs.backgroundColor ?? attrs['background-color']
}

function normalizeLegacyMenuProps(attrs = {}) {
  const menuProps = attrs.menuProps ?? attrs['menu-props']
  const normalizedMenuProps =
    typeof menuProps === 'string'
      ? menuProps.trim()
        ? { contentClass: menuProps }
        : undefined
      : menuProps

  return {
    ...(normalizedMenuProps || {}),
    contentClass: ['v-menu__content', normalizedMenuProps?.contentClass]
      .filter(Boolean)
      .join(' '),
    location: normalizedMenuProps?.location ?? 'bottom start',
    origin: normalizedMenuProps?.origin ?? 'top start',
    offset: normalizedMenuProps?.offset ?? 0,
    scrollStrategy: normalizedMenuProps?.scrollStrategy ?? 'reposition',
    transition: normalizedMenuProps?.transition ?? false
  }
}

function stripLegacyInputAttrs(attrs = {}) {
  const normalized = { ...attrs }

  if (attrs['hide-details'] != null && normalized.hideDetails == null) {
    normalized.hideDetails = hasBooleanishAttr(attrs['hide-details'])
      ? true
      : attrs['hide-details']
  }

  if (attrs['single-line'] != null && normalized.singleLine == null) {
    normalized.singleLine = hasBooleanishAttr(attrs['single-line'])
      ? true
      : attrs['single-line']
  }

  if (attrs['append-icon'] != null && normalized.appendIcon == null) {
    normalized.appendIcon = attrs['append-icon']
  }

  if (
    attrs['append-inner-icon'] != null &&
    normalized.appendInnerIcon == null
  ) {
    normalized.appendInnerIcon = attrs['append-inner-icon']
  }

  if (attrs['append-outer-icon'] != null && normalized.appendIcon == null) {
    normalized.appendIcon = attrs['append-outer-icon']
  }

  if (attrs['prepend-icon'] != null && normalized.prependIcon == null) {
    normalized.prependIcon = attrs['prepend-icon']
  }

  if (
    attrs['prepend-inner-icon'] != null &&
    normalized.prependInnerIcon == null
  ) {
    normalized.prependInnerIcon = attrs['prepend-inner-icon']
  }

  // Vuetify 2's select/autocomplete had two boolean props for rendering
  // selections as chips: `chips` and `small-chips` (both just turned chip
  // mode on; `small-chips` additionally sized them down). Vuetify 4 only
  // has one boolean, `chips`, and no small variant - so `small-chips` has
  // no matching prop name and was passing straight through as an unknown
  // HTML attribute, silently never turning chip mode on. Same story for
  // `deletable-chips` -> `closableChips`. Without this, every legacy
  // `small-chips`/`deletable-chips` multi-select falls back to Vuetify 4's
  // plain-text selection display, which for `multiple` renders every
  // selected item joined together with no chip boundaries at all.
  if (
    (hasBooleanishAttr(attrs['small-chips']) ||
      hasBooleanishAttr(attrs.chips)) &&
    normalized.chips !== true
  ) {
    normalized.chips = true
  }

  if (hasBooleanishAttr(attrs['deletable-chips'])) {
    normalized.chips = true
    normalized.closableChips = true
  }

  if (
    attrs['onClick:append'] != null &&
    normalized['onClick:appendInner'] == null
  ) {
    normalized['onClick:appendInner'] = attrs['onClick:append']
  }

  if (
    attrs['onClick:append-outer'] != null &&
    normalized['onClick:append'] == null
  ) {
    normalized['onClick:append'] = attrs['onClick:append-outer']
  }

  if (
    attrs['onClick:prepend-inner'] != null &&
    normalized['onClick:prependInner'] == null
  ) {
    normalized['onClick:prependInner'] = attrs['onClick:prepend-inner']
  }

  delete normalized.solo
  delete normalized['solo-inverted']
  delete normalized.outlined
  delete normalized.filled
  delete normalized.flat
  delete normalized.plain
  delete normalized.underlined
  delete normalized.dense
  delete normalized['small-chips']
  delete normalized['deletable-chips']
  delete normalized['hide-details']
  delete normalized['single-line']
  delete normalized['append-icon']
  delete normalized['append-inner-icon']
  delete normalized['append-outer-icon']
  delete normalized['prepend-icon']
  delete normalized['prepend-inner-icon']
  delete normalized['onClick:append-outer']
  delete normalized['onClick:prepend-inner']
  delete normalized.backgroundColor
  delete normalized['background-color']
  delete normalized.attach
  return normalized
}

function hasLegacyModelValue(value) {
  if (Array.isArray(value)) return value.length > 0
  return value !== null && value !== undefined && value !== ''
}

function resolveLegacyModelValue(attrs = {}) {
  // Components in this file deliberately declare no `props` schema (see the
  // COMPONENT_V_MODEL note above) so every binding falls through to `attrs`
  // verbatim. `v-model="x"` always compiles to the literal camelCase
  // `modelValue`, but an explicit `:model-value="x"` template binding (used
  // instead of v-model by several consumers, e.g. FlewidTextField.vue) stays
  // in its original kebab-case form here since there's no declared prop for
  // Vue to camelCase it against - so `attrs.modelValue` alone silently never
  // matches and the bound value is lost.
  return attrs.modelValue ?? attrs['model-value'] ?? attrs.value
}

function withLegacyDirtyClass(normalizedAttrs = {}, attrs = {}) {
  return {
    ...normalizedAttrs,
    class: [
      normalizedAttrs.class,
      {
        'v-input--legacy-dirty': hasLegacyModelValue(
          resolveLegacyModelValue(attrs)
        )
      }
    ]
  }
}

export {
  hasLegacyModelValue,
  hasBooleanishAttr,
  normalizeLegacyMenuProps,
  resolveLegacyModelValue,
  resolveVariant,
  resolveDensity,
  resolveBgColor,
  stripLegacyInputAttrs,
  withLegacyDirtyClass
}
